/**
 * =============================================================================
 * Institution users & invitations i18n (SK + EN) — FE issue #27 / BE #61
 * =============================================================================
 *
 * Texts for provisioning institution admins, invite / notification mail
 * outcomes and the invite status of institution users. Language resolution
 * is shared with `promotionI18n.ts` (stored preference > browser locale).
 *
 * Mail outcome contract (BE #61): every endpoint that triggers a mail returns
 * `mail_status` = sent | disabled | failed and an optional `mail_error`.
 * "disabled" and "failed" must never be presented as success.
 */

import { getLang, type Lang } from './promotionI18n'

export type { Lang }

interface BiText {
  sk: string
  en: string
}

const TEXTS = {
  // --- Clinic portal clarification ---------------------------------------
  clinicLoginHint: {
    sk: 'Používatelia inštitúcie (administrátori a lekári) sa prihlasujú do KLINICKÉHO portálu (RehaPiano Clinic), nie do tohto admin portálu.',
    en: 'Institution users (administrators and doctors) sign in to the CLINIC portal (RehaPiano Clinic), not to this admin portal.',
  },
  contactEmailLabel: { sk: 'Kontaktný e-mail inštitúcie *', en: 'Institution contact e-mail *' },
  contactEmailHint: {
    sk: 'Len kontakt — nikto sa ním neprihlasuje. Prístup do klinického portálu má až vytvorený používateľ.',
    en: 'Contact only — nobody signs in with it. Clinic portal access needs an institution user.',
  },
  notificationEmailLabel: { sk: 'E-mail pre notifikácie', en: 'Notification e-mail' },

  // --- First admin provisioning --------------------------------------------
  firstAdminTitle: { sk: 'Prvý administrátor inštitúcie', en: 'First institution administrator' },
  firstAdminToggle: {
    sk: 'Vytvoriť administrátora inštitúcie a poslať mu pozvánku',
    en: 'Create an institution administrator and send an invitation',
  },
  firstAdminHint: {
    sk: 'Dostane e-mail s odkazom na aktiváciu účtu (platný 48 h) a nastavenie hesla v klinickom portáli.',
    en: 'They receive an e-mail with a link (valid 48 h) to activate the account and set a password in the clinic portal.',
  },
  adminEmailLabel: { sk: 'E-mail administrátora *', en: 'Administrator e-mail *' },
  adminFirstName: { sk: 'Meno (voliteľné)', en: 'First name (optional)' },
  adminLastName: { sk: 'Priezvisko (voliteľné)', en: 'Last name (optional)' },
  adminNameHint: {
    sk: 'Ak meno nevyplníte, odvodí sa z e-mailu; používateľ si ho opraví pri aktivácii.',
    en: 'If empty, the name is derived from the e-mail; the user can correct it on activation.',
  },
  inviteLanguage: { sk: 'Jazyk pozvánky', en: 'Invitation language' },
  addAdminAction: { sk: 'Pridať admina', en: 'Add admin' },
  addAdminTitle: { sk: 'Pridať administrátora inštitúcie', en: 'Add institution administrator' },
  addAdminSubmit: { sk: 'Vytvoriť a poslať pozvánku', en: 'Create and send invitation' },
  submitting: { sk: 'Odosiela sa…', en: 'Sending…' },
  cancel: { sk: 'Zrušiť', en: 'Cancel' },

  // --- Existing user conflict ---------------------------------------------
  userExistsTitle: { sk: 'Používateľ s týmto e-mailom už existuje', en: 'A user with this e-mail already exists' },
  userExistsBody: {
    sk: '{email} je teraz {role} v inštitúcii „{institution}“. Priradiť ho k tejto inštitúcii ako administrátora? Z pôvodnej inštitúcie bude presunutý a dostane notifikačný e-mail.',
    en: '{email} is currently {role} at “{institution}”. Assign them to this institution as administrator? They will be moved from their current institution and notified by e-mail.',
  },
  userExistsNoInstitution: { sk: 'bez inštitúcie', en: 'no institution' },
  assignAction: { sk: 'Priradiť a notifikovať', en: 'Assign and notify' },
  skipAdminAction: { sk: 'Pokračovať bez administrátora', en: 'Continue without an administrator' },
  userNotAssignable: {
    sk: 'E-mail {email} patrí platformovému účtu ({role}) — nemôže byť administrátorom inštitúcie. Použite iný e-mail.',
    en: 'The e-mail {email} belongs to a platform account ({role}) and cannot become an institution administrator. Use another e-mail.',
  },

  // --- Outcomes -------------------------------------------------------------
  institutionCreated: { sk: 'Inštitúcia „{name}“ bola vytvorená.', en: 'Institution “{name}” was created.' },
  inviteSentTo: {
    sk: 'Pozvánka odoslaná na {email} — po aktivácii sa prihlási do klinického portálu.',
    en: 'Invitation sent to {email} — after activation they sign in to the clinic portal.',
  },
  assignmentSentTo: {
    sk: '{email} bol priradený k inštitúcii; notifikácia odoslaná.',
    en: '{email} was assigned to the institution; notification sent.',
  },
  assignedInviteSentTo: {
    sk: '{email} bol priradený k inštitúcii; keďže účet ešte nie je aktivovaný, dostal novú pozvánku.',
    en: '{email} was assigned to the institution; the account is not activated yet, so a new invitation was sent.',
  },
  mailDisabled: {
    sk: 'E-mail pre {email} NEBOL odoslaný: odosielanie e-mailov je na serveri vypnuté (SMTP_ENABLED=false). Pozvánku pošlite znova po zapnutí SMTP alebo nastavte heslo ručne.',
    en: 'The e-mail to {email} was NOT sent: e-mail sending is disabled on the server (SMTP_ENABLED=false). Resend once SMTP is enabled or set a password manually.',
  },
  mailFailed: {
    sk: 'E-mail pre {email} sa NEPODARILO odoslať ({reason}). Skontrolujte SMTP a použite „Znovu poslať pozvánku“.',
    en: 'The e-mail to {email} could NOT be sent ({reason}). Check SMTP and use “Resend invitation”.',
  },

  // --- Users list -----------------------------------------------------------
  searchPlaceholder: {
    sk: 'Hľadať podľa e-mailu alebo mena…',
    en: 'Search by e-mail or name…',
  },
  searchLabel: { sk: 'Hľadať', en: 'Search' },
  filterInstitution: { sk: 'Inštitúcia', en: 'Institution' },
  filterAllInstitutions: { sk: 'Všetky inštitúcie', en: 'All institutions' },
  noUsersFound: { sk: 'Žiadni používatelia nezodpovedajú hľadaniu', en: 'No users match the search' },
  inviteColumn: { sk: 'Pozvánka', en: 'Invitation' },
  inviteActive: { sk: 'Aktivovaný', en: 'Activated' },
  invitePending: { sk: 'Čaká na aktiváciu', en: 'Pending activation' },
  inviteExpired: { sk: 'Pozvánka vypršala', en: 'Invitation expired' },
  inviteNotDelivered: { sk: 'Pozvánka nedoručená', en: 'Invitation not delivered' },
  inviteNone: { sk: 'Bez pozvánky', en: 'Not invited' },
  inviteSentAt: { sk: 'odoslaná {date}', en: 'sent {date}' },
  inviteExpiresAt: { sk: 'platí do {date}', en: 'valid until {date}' },
  resendInvite: { sk: 'Znovu poslať pozvánku', en: 'Resend invitation' },
  resendConfirm: {
    sk: 'Znovu poslať pozvánku na {email}? Predchádzajúce pozvánkové odkazy prestanú platiť.',
    en: 'Resend the invitation to {email}? Previous invitation links stop working.',
  },
  resendSent: { sk: 'Pozvánka bola znovu odoslaná na {email}.', en: 'The invitation was resent to {email}.' },
  movedNotificationSent: {
    sk: 'Zmeny boli uložené. {email} dostal e-mail o priradení k inštitúcii „{institution}“.',
    en: 'Changes saved. {email} was notified about the assignment to “{institution}”.',
  },
  movedInviteSent: {
    sk: 'Zmeny boli uložené. {email} ešte nemá aktivovaný účet — dostal novú pozvánku pre inštitúciu „{institution}“.',
    en: 'Changes saved. {email} has not activated the account yet — a new invitation for “{institution}” was sent.',
  },
  moveHint: {
    sk: 'Pri zmene inštitúcie dostane používateľ notifikačný e-mail.',
    en: 'Changing the institution sends the user a notification e-mail.',
  },
  // --- Institutions page -------------------------------------------------
  institutionsTitle: { sk: 'Inštitúcie', en: 'Institutions' },
  institutionsIntro: { sk: 'Správa inštitúcií platformy.', en: 'Manage institutions stored in the platform.' },
  addInstitution: { sk: '+ Pridať inštitúciu', en: '+ Add Institution' },
  createInstitutionTitle: { sk: 'Vytvoriť inštitúciu', en: 'Create Institution' },
  editInstitutionTitle: { sk: 'Upraviť inštitúciu', en: 'Edit Institution' },
  fieldCode: { sk: 'Kód *', en: 'Code *' },
  fieldCodeHint: { sk: 'Jedinečný identifikátor používaný v integráciách.', en: 'Unique identifier used in integrations.' },
  fieldInstitutionName: { sk: 'Názov inštitúcie *', en: 'Institution Name *' },
  fieldInstitutionType: { sk: 'Typ inštitúcie', en: 'Institution Type' },
  fieldAddress: { sk: 'Adresa', en: 'Address' },
  fieldCountry: { sk: 'Krajina', en: 'Country' },
  fieldStatus: { sk: 'Stav', en: 'Status' },
  statusActive: { sk: 'Aktívna', en: 'Active' },
  statusInactive: { sk: 'Neaktívna', en: 'Inactive' },
  create: { sk: 'Vytvoriť', en: 'Create' },
  update: { sk: 'Uložiť', en: 'Update' },
  saving: { sk: 'Ukladá sa…', en: 'Saving...' },
  colInstitution: { sk: 'Inštitúcia', en: 'Institution' },
  colType: { sk: 'Typ', en: 'Type' },
  colCountry: { sk: 'Krajina', en: 'Country' },
  colContact: { sk: 'Kontaktný e-mail', en: 'Contact e-mail' },
  colStatus: { sk: 'Stav', en: 'Status' },
  colDevices: { sk: 'Zariadenia', en: 'Devices' },
  colUsers: { sk: 'Používatelia', en: 'Users' },
  colActions: { sk: 'Akcie', en: 'Actions' },
  edit: { sk: 'Upraviť', en: 'Edit' },
  deactivate: { sk: 'Deaktivovať', en: 'Deactivate' },
  activate: { sk: 'Aktivovať', en: 'Activate' },
  noInstitutions: { sk: 'Žiadne inštitúcie', en: 'No institutions found' },
  institutionSaveFailed: { sk: 'Inštitúciu sa nepodarilo uložiť.', en: 'The institution could not be saved.' },
  institutionDuplicate: {
    sk: 'Inštitúcia s týmto kódom alebo kontaktným e-mailom už existuje.',
    en: 'An institution with this code or contact e-mail already exists.',
  },
  adminCreateFailed: { sk: 'Administrátora sa nepodarilo vytvoriť.', en: 'The administrator could not be created.' },
  roleInstitutionAdmin: { sk: 'administrátor inštitúcie', en: 'institution administrator' },
  roleDoctor: { sk: 'lekár', en: 'doctor' },
} satisfies Record<string, BiText>

export type UsersTextKey = keyof typeof TEXTS

/** Translate a users/invitations string, filling `{placeholders}`. */
export function ut(
  key: UsersTextKey,
  vars: Record<string, string | number | null | undefined> = {},
  lang: Lang = getLang(),
): string {
  return TEXTS[key][lang].replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? ''))
}

export type MailStatus = 'sent' | 'disabled' | 'failed'

export interface MailOutcome {
  mail_status?: MailStatus | string | null
  mail_error?: string | null
}

/** Severity of a mail outcome for banner colouring. */
export function mailSeverity(outcome: MailOutcome | null | undefined): 'success' | 'error' | 'none' {
  if (!outcome?.mail_status) return 'none'
  return outcome.mail_status === 'sent' ? 'success' : 'error'
}

/** Message for an undelivered mail, or null when it was delivered / not attempted. */
export function mailProblemMessage(email: string, outcome: MailOutcome | null | undefined): string | null {
  if (!outcome?.mail_status || outcome.mail_status === 'sent') return null
  if (outcome.mail_status === 'disabled') return ut('mailDisabled', { email })
  return ut('mailFailed', { email, reason: outcome.mail_error || 'unknown' })
}

export type InviteStatus = 'active' | 'pending' | 'expired' | 'not_delivered' | 'not_invited'

const INVITE_KEYS: Record<InviteStatus, UsersTextKey> = {
  active: 'inviteActive',
  pending: 'invitePending',
  expired: 'inviteExpired',
  not_delivered: 'inviteNotDelivered',
  not_invited: 'inviteNone',
}

export function inviteStatusLabel(status: string | null | undefined): string {
  const key = INVITE_KEYS[(status || 'not_invited') as InviteStatus] || 'inviteNone'
  return ut(key)
}

export function roleLabel(role: string | null | undefined): string {
  if (role === 'institution_admin') return ut('roleInstitutionAdmin')
  if (role === 'doctor') return ut('roleDoctor')
  return role || ''
}

/** Structured 409 detail of the institution-admin provisioning (BE #61). */
export interface ProvisionConflict {
  code: 'user_exists' | 'user_not_assignable' | string
  message?: string
  message_sk?: string
  user?: {
    id: string
    email: string
    role: string
    institution_id?: string | null
    institution_name?: string | null
    has_password?: boolean
  }
}

/** Extract the provisioning conflict from an axios error, if it is one. */
export function provisionConflict(err: unknown): ProvisionConflict | null {
  const error = err as { response?: { status?: number; data?: { detail?: unknown } } }
  const detail = error?.response?.data?.detail
  if (error?.response?.status === 409 && detail && typeof detail === 'object' && 'code' in detail) {
    return detail as ProvisionConflict
  }
  return null
}
